import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Globe, LogOut, User, Settings, ShoppingCart, MessageCircle, Package, Heart, Menu, Bell } from 'lucide-react';
import { useChatNotifications } from '@/hooks/useChatNotifications';
import { useChatUnreadCount } from '@/hooks/useChatUnreadCount';
import { useNotifications } from '@/hooks/useNotifications';
import { useIsMobile } from '@/hooks/use-mobile';
import { useCartCount } from '@/hooks/useCartCount';
import { useCurrentUser } from '@/contexts/UserContext';
import { useWishlist } from '@/hooks/useWishlist';
import IsraelFlag from '@/components/IsraelFlag';
import MobileMenu from '@/components/mobile/MobileMenu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { HeaderSearch } from '@/components/header/HeaderSearch';

export default function Navigation() {
  const { i18n, t } = useTranslation('common');
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { user, isAdmin, isSeller } = useCurrentUser();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { unreadCount } = useChatNotifications();
  const { unreadCount: chatUnreadCount } = useChatUnreadCount();
  const { unreadCount: notificationUnreadCount } = useNotifications();
  const { count: cartCount } = useCartCount();
  const { wishlist } = useWishlist();
  const wishlistCount = wishlist?.length || 0;

  // No more useEffect needed - user data comes from context

  const handleLogout = async () => {
    await supabase.auth.signOut();
    toast.success(t('nav.logout'));
    window.location.href = '/';
  };

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    document.documentElement.dir = lang === 'he' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  };

  return (
    <>
      <MobileMenu open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      <header className="app-header border-b bg-card sticky top-0 z-[1000] shadow-sm backdrop-blur-md">
        <div className="container mx-auto px-2 sm:px-4 py-2 sm:py-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4">
              {isMobile && (
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-8 w-8 flex-shrink-0"
                  onClick={() => setDrawerOpen(true)}
                >
                  <Menu className="w-5 h-5" />
                </Button>
              )}
              <Link to="/" className="flex items-center gap-1 sm:gap-2 text-base sm:text-xl font-bold text-primary min-w-0 flex-shrink">
                <IsraelFlag className="w-6 h-4 sm:w-7 sm:h-5 flex-shrink-0" />
                <span className="truncate">{t('appName')}</span>
              </Link>
              
              {!isMobile && (
                <nav className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" asChild>
                    <Link to="/stays">{t('common:nav.stays', 'Stays')}</Link>
                  </Button>
                  <Button variant="ghost" size="sm" asChild>
                    <Link to="/catalog">{t('common:nav.shop', 'Shop')}</Link>
                  </Button>
                  <Button variant="ghost" size="sm" asChild>
                    <Link to="/bundles">{t('common:nav.bundles', 'Bundles')}</Link>
                  </Button>
                </nav>
              )}
            </div>

          <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
            {/* Orders & Wishlist Icons */}
            {user && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 sm:h-10 sm:w-10"
                  onClick={() => navigate('/orders')}
                >
                  <Package className="w-5 h-5" />
                </Button>
                <div className="relative">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 sm:h-10 sm:w-10"
                    onClick={() => navigate('/wishlist')}
                  >
                    <Heart className="w-5 h-5" />
                  </Button>
                  {wishlistCount > 0 && (
                    <Badge
                      variant="destructive"
                      className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
                    >
                      {wishlistCount > 9 ? '9+' : wishlistCount}
                    </Badge>
                  )}
                </div>
                <div className="relative">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 sm:h-10 sm:w-10"
                    onClick={() => navigate('/buyer/chats')}
                  >
                    <MessageCircle className="w-5 h-5" />
                  </Button>
                  {chatUnreadCount > 0 && (
                    <Badge
                      variant="destructive"
                      className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
                    >
                      {chatUnreadCount}
                    </Badge>
                  )}
                </div>
              </>
            )}

            {/* Cart Icon */}
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 sm:h-10 sm:w-10"
                onClick={() => navigate('/cart')}
              >
                <ShoppingCart className="w-5 h-5" />
              </Button>
              {cartCount > 0 && (
                <Badge
                  variant="destructive"
                  className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
                >
                  {cartCount}
                </Badge>
              )}
            </div>

            {/* Compact header search (desktop only) */}
            <HeaderSearch />

            {/* Language Selector */}
            <Select value={i18n.language} onValueChange={changeLanguage}>
              <SelectTrigger className="w-[100px] sm:w-[120px] h-8 sm:h-9 text-xs sm:text-sm">
                <Globe className="w-4 h-4 sm:w-5 sm:h-5 mr-1" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="he">עברית</SelectItem>
                <SelectItem value="ru">Русский</SelectItem>
                <SelectItem value="es">Español</SelectItem>
              </SelectContent>
            </Select>
          </div>

            {/* Notifications Bell */}
            {user && (
              <Button 
                variant="ghost" 
                size="icon"
                className="relative h-8 w-8 sm:h-9 sm:w-9"
                onClick={() => {
                  if (isSeller) {
                    navigate('/seller/notifications');
                  } else if (isAdmin) {
                    navigate('/admin'); // Admin notifications can be added later
                  } else {
                    navigate('/account/notification-center');
                  }
                }}
              >
                <Bell className="w-4 h-4 sm:w-5 sm:h-5" />
                {notificationUnreadCount > 0 && (
                  <Badge 
                    variant="destructive" 
                    className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
                  >
                    {notificationUnreadCount > 9 ? '9+' : notificationUnreadCount}
                  </Badge>
                )}
              </Button>
            )}

            {/* User Menu */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8 sm:h-9">
                    <User className="w-4 h-4" />
                    <span className="hidden sm:inline sm:ml-2">{t('nav.profile')}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {isSeller && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                        <Settings className="w-4 h-4 mr-2" />
                        {t('nav.dashboard')}
                        {unreadCount > 0 && (
                          <Badge variant="destructive" className="ml-auto">
                            {unreadCount}
                          </Badge>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/dashboard/chats')}>
                        <MessageCircle className="w-4 h-4 mr-2" />
                        {t('nav.chats')}
                        {unreadCount > 0 && (
                          <Badge variant="destructive" className="ml-auto">
                            {unreadCount}
                          </Badge>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  {!isSeller && !isAdmin && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/account')}>
                        <User className="w-4 h-4 mr-2" />
                        {t('nav.myAccount') || 'My Account'}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/seller-onboarding')}>
                        <Package className="w-4 h-4 mr-2" />
                        {t('nav.becomeSeller')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  {isAdmin && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/admin')}>
                        <Settings className="w-4 h-4 mr-2" />
                        {t('nav.admin')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/auth">
                <Button size="sm">{t('actions.signIn')}</Button>
              </Link>
            )}
          </div>
        </div>
    </header>
    </>
  );
}
